import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import ImageUpload from "./ImageUpload";

const DESTS = ["Dubai", "Singapore", "Thailand", "Bali", "Vietnam", "Maldives", "Mauritius", "Europe", "Australia", "Andaman", "Kashmir", "Goa", "Kerala", "Himachal", "Uttarakhand", "Bhutan", "Nepal"];

const EMPTY = {
  title: "", destination: "Dubai", category: "international", country: "",
  duration: "", nights: 5, days: 6, price: "", image: "",
  description: "", inclusions: "", highlights: "", featured: false, active: true,
};

export default function PackageFormModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? {
    ...EMPTY, ...initial,
    price: String(initial.price),
    inclusions: (initial.inclusions || []).join(", "),
    highlights: (initial.highlights || []).join(", "),
  } : EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setF({ ...f, [name]: type === "checkbox" ? checked : value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.price || !f.image) { toast.error("Title, price and image are required"); return; }
    setLoading(true);
    const payload = {
      title: f.title,
      destination: f.destination,
      category: f.category,
      country: f.country,
      duration: f.duration,
      nights: Number(f.nights) || 0,
      days: Number(f.days) || 0,
      price: Number(f.price) || 0,
      image: f.image,
      description: f.description,
      inclusions: f.inclusions.split(",").map((s) => s.trim()).filter(Boolean),
      highlights: f.highlights.split(",").map((s) => s.trim()).filter(Boolean),
      featured: !!f.featured,
      active: !!f.active,
    };
    try {
      if (initial) {
        await api.patch(`/admin/packages/${initial.id}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/admin/packages", payload);
        toast.success("Package created");
      }
      onSaved();
      onClose();
    } catch {
      toast.error("Save failed. Check all fields.");
    } finally { setLoading(false); }
  };

  const input = "w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#006CE4] focus:ring-4 focus:ring-blue-100 transition";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="pkg-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h3 className="h3">{initial ? "Edit Package" : "Add New Package"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" data-testid="pkg-form-close" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Package Title *</label><input required name="title" value={f.title} onChange={onChange} className={input} placeholder="e.g. Maldives Luxury Escape" data-testid="pkg-form-title" /></div>
          <div><label className="text-xs font-semibold muted">Destination</label>
            <select name="destination" value={f.destination} onChange={onChange} className={input} data-testid="pkg-form-destination">
              {DESTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-semibold muted">Category</label>
            <select name="category" value={f.category} onChange={onChange} className={input} data-testid="pkg-form-category">
              <option value="international">International</option>
              <option value="domestic">Domestic</option>
            </select>
          </div>
          <div><label className="text-xs font-semibold muted">Country</label><input name="country" value={f.country} onChange={onChange} className={input} placeholder="e.g. Maldives" data-testid="pkg-form-country" /></div>
          <div><label className="text-xs font-semibold muted">Duration</label><input name="duration" value={f.duration} onChange={onChange} className={input} placeholder="e.g. 5N/6D" data-testid="pkg-form-duration" /></div>
          <div><label className="text-xs font-semibold muted">Nights</label><input type="number" name="nights" value={f.nights} onChange={onChange} className={input} data-testid="pkg-form-nights" /></div>
          <div><label className="text-xs font-semibold muted">Days</label><input type="number" name="days" value={f.days} onChange={onChange} className={input} data-testid="pkg-form-days" /></div>
          <div className="sm:col-span-2 flex items-end gap-4"><div className="flex-1"><label className="text-xs font-semibold muted">Price per person (₹) *</label><input required type="number" name="price" value={f.price} onChange={onChange} className={input} placeholder="e.g. 89999" data-testid="pkg-form-price" /></div></div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold muted">Cover Image *</label>
            <ImageUpload value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="pkg-form-image" />
          </div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Description</label><textarea name="description" value={f.description} onChange={onChange} rows="2" className={`${input} resize-none`} data-testid="pkg-form-description" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Inclusions (comma separated)</label><input name="inclusions" value={f.inclusions} onChange={onChange} className={input} placeholder="4-star hotels, Daily breakfast, Airport transfers" data-testid="pkg-form-inclusions" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Highlights (comma separated)</label><input name="highlights" value={f.highlights} onChange={onChange} className={input} placeholder="Guided sightseeing, 24x7 support" data-testid="pkg-form-highlights" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="featured" checked={!!f.featured} onChange={onChange} className="accent-[#FF7D00]" data-testid="pkg-form-featured" /> Featured on homepage</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="active" checked={!!f.active} onChange={onChange} className="accent-[#FF7D00]" data-testid="pkg-form-active" /> Active (visible on site)</label>
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline flex-1 justify-center" data-testid="pkg-form-cancel">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid="pkg-form-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : initial ? "Save Changes" : "Create Package"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
